/**
 * The pickup, as this phone sees it.
 *
 * All of it lives on the server, so this hook is a window rather than a store: a reload, a swap
 * to Google Maps, or a dismissed notification all leave the meeting exactly where it was. What
 * is local is only the polling and the throttle on how often a fix is worth sending.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Coordinates, PickupAckStep, PickupPunctuality, PickupState } from "../shared/types";

/**
 * Fifteen seconds each way.
 *
 * Fresh enough that Eben's figure moves while he watches it, and cheap: a fix is a small write,
 * and the routing behind it reuses a cached anchor rather than asking Google again.
 */
const POLL_MS = 15_000;

export interface PickupControls {
	pickup: PickupState | null;
	refresh: () => void;
	refreshing: boolean;
	error: string | null;
	saving: boolean;
	ack: (step: PickupAckStep) => void;
	setPunctuality: (status: PickupPunctuality) => void;
	markCommunicationShown: () => void;
	/** When a poll last came back, however empty. Separates a failed fetch from an absent record. */
	polledAtMs: number | null;
	/**
	 * Whether this phone has asked at all yet — true even when the ask failed.
	 *
	 * Before the first answer, "no pickup" and "do not know" look identical, and the lock screen
	 * acted on the wrong one: a cold start fell through to the ordinary journey, whose activity key
	 * differs, so the shell took down the pickup card it was correctly showing and put it back a few
	 * seconds later. Distinct from `polledAtMs` on purpose — a permanently failing endpoint must not
	 * silence the card forever.
	 */
	settled: boolean;
	/** Start, or resume after exiting. */
	enter: () => void;
	/** Stop sharing. Deliberately not a cancellation. */
	exit: () => void;
	markDriving: () => void;
	/** Swipe the deck. Stored, so the leave nudge is about the train on screen. */
	choose: (planId: string) => void;
	/** Parked where they always meet, which is more than merely being nearby. */
	arrive: () => void;
	complete: () => void;
	cancel: () => void;
	/** The rider's own two. The offer still raises itself; these are for when the fence is wrong. */
	request: () => void;
	standDown: () => void;
}

export function usePickup(deviceId: string, position: Coordinates | null): PickupControls {
	const [pickup, setPickup] = useState<PickupState | null>(null);
	const [polledAtMs, setPolledAtMs] = useState<number | null>(null);
	const [settled, setSettled] = useState(false);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const writingRef = useRef(false);
	const readVersionRef = useRef(0);
	const readControllerRef = useRef<AbortController | null>(null);
	const postedAtRef = useRef(0);
	const enteredFromLinkRef = useRef(false);

	const load = useCallback(async () => {
		if (writingRef.current) return;
		const version = ++readVersionRef.current;
		readControllerRef.current?.abort();
		const controller = new AbortController();
		readControllerRef.current = controller;
		const timeout = window.setTimeout(() => controller.abort(), 10_000);
		setRefreshing(true);
		setError(null);
		try {
			const response = await fetch(`/api/pickup?deviceId=${deviceId}`, { cache: "no-store", signal: controller.signal });
			if (!response.ok) {
				throw new Error("Pickup refresh failed");
			}
			const body = (await response.json()) as { pickup: PickupState | null };
			if (version !== readVersionRef.current) return;
			setPickup(body.pickup ?? null);
			setPolledAtMs(Date.now());
		} catch {
			if (version === readVersionRef.current) setError("Couldn't refresh pickup. Showing the last update.");
		} finally {
			window.clearTimeout(timeout);
			if (version === readVersionRef.current) {
				setRefreshing(false);
				setSettled(true);
			}
		}
	}, [deviceId]);

	const send = useCallback(
		async (body: Record<string, unknown>) => {
			if (writingRef.current) return;
			writingRef.current = true;
			const version = ++readVersionRef.current;
			readControllerRef.current?.abort();
			setRefreshing(false);
			const feedback = !!body.action && body.action !== "communication-seen";
			setSaving(true);
			if (feedback) setError(null);
			const controller = new AbortController();
			readControllerRef.current = controller;
			const timeout = window.setTimeout(() => controller.abort(), 10_000);
			try {
				const response = await fetch("/api/pickup", {
					method: "POST",
					signal: controller.signal,
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ deviceId, ...body }),
				});
				const parsed = (await response.json()) as { pickup?: PickupState | null; error?: string };
				if (version !== readVersionRef.current) return;
				if (response.ok || response.status === 409) setPickup(parsed.pickup ?? null);
				if (!response.ok) throw new Error(typeof parsed.error === "string" ? parsed.error : "Couldn't confirm pickup update. Refresh before retrying.");
			} catch (cause) {
				if (feedback && version === readVersionRef.current) setError(cause instanceof Error && cause.name !== "AbortError" ? cause.message : "Couldn't confirm pickup update. Refresh and review before retrying.");
			} finally {
				window.clearTimeout(timeout);
				writingRef.current = false;
				if (version === readVersionRef.current) {
					setSaving(false);
					setSettled(true);
				}
			}
		},
		[deviceId],
	);

	useEffect(() => {
		void load();
		const timer = window.setInterval(() => {
			if (document.visibilityState === "visible") {
				void load();
			}
		}, POLL_MS);
		// Coming back from Google Maps is the case that matters: the app was frozen, so the first
		// thing it owes the screen is a fresh reading.
		const onVisible = () => {
			if (document.visibilityState === "visible") {
				void load();
			}
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			++readVersionRef.current;
			readControllerRef.current?.abort();
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [load]);

	// Tapping the notification is a decision, so it enters outright. Opening the app any other
	// way only offers the button.
	useEffect(() => {
		if (enteredFromLinkRef.current || !new URLSearchParams(window.location.search).get("pickup")) {
			return;
		}
		enteredFromLinkRef.current = true;
		void send({ action: "enter" });
		const url = new URL(window.location.href);
		url.searchParams.delete("pickup");
		window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
	}, [send]);

	useEffect(() => {
		if (!pickup || pickup.role !== "driver" || !pickup.sharing || !position) {
			return;
		}
		if (pickup.status === "completed" || pickup.status === "cancelled") {
			return;
		}
		const nowMs = Date.now();
		if (nowMs - postedAtRef.current < POLL_MS) {
			return;
		}
		postedAtRef.current = nowMs;
		void send({ lat: position.lat, lon: position.lon });
	}, [pickup, position, send]);

	return {
		pickup,
		refresh: useCallback(() => void load(), [load]),
		refreshing,
		error,
		saving,
		ack: useCallback((step: PickupAckStep) => {
			if (pickup) void send({ action: "ack", pickupId: pickup.id, expectedRevision: pickup.communication?.revision ?? 0, step });
		}, [pickup, send]),
		setPunctuality: useCallback((status: PickupPunctuality) => {
			if (pickup) void send({ action: "punctuality", pickupId: pickup.id, expectedRevision: pickup.communication?.revision ?? 0, punctuality: status });
		}, [pickup, send]),
		markCommunicationShown: useCallback(() => {
			if (pickup?.role === "rider" && pickup.communication && pickup.communication.shownRevision < pickup.communication.revision) {
				void send({ action: "communication-seen", pickupId: pickup.id, expectedRevision: pickup.communication.revision });
			}
		}, [pickup, send]),
		polledAtMs,
		settled,
		enter: useCallback(() => void send({ action: "enter" }), [send]),
		exit: useCallback(() => void send({ action: "exit" }), [send]),
		markDriving: useCallback(() => void send({ action: "driving" }), [send]),
		choose: useCallback((planId: string) => void send({ action: "choose", planId }), [send]),
		// The only action that buzzes the rider, so it carries the position that proves it: the
		// server refuses the buzz when the fix says the car is nowhere near the kerb.
		arrive: useCallback(
			() => void send(position ? { action: "here", lat: position.lat, lon: position.lon } : { action: "here" }),
			[send, position],
		),
		complete: useCallback(() => void send({ action: "done" }), [send]),
		cancel: useCallback(() => void send({ action: "cancel" }), [send]),
		request: useCallback(() => void send({ action: "request" }), [send]),
		standDown: useCallback(() => void send({ action: "standdown" }), [send]),
	};
}
